/**
 * useTicketConstants Hook
 * Fetches ticket constants from the server API
 * Provides loading, error states, and caching
 */
import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

// Default fallback constants (used while loading or on error)
const DEFAULT_CONSTANTS = {
  workflowState: {
    OPEN: "open",
    IN_PROGRESS: "in_progress",
    ON_HOLD: "on_hold",
    RESOLVED: "resolved",
    CLOSED: "closed",
  },
  workflowStates: ["open", "in_progress", "on_hold", "resolved", "closed"],
  workflowStateLabels: {
    open: "Open",
    in_progress: "In Progress",
    on_hold: "On Hold",
    resolved: "Resolved",
    closed: "Closed",
  },
  statusTabs: [
    { label: "All", value: "" },
    { label: "Open", value: "open" },
    { label: "In Progress", value: "in_progress" },
    { label: "On Hold", value: "on_hold" },
    { label: "Resolved", value: "resolved" },
    { label: "Closed", value: "closed" },
  ],
  priority: {
    LOW: "Low",
    MEDIUM: "Medium",
    HIGH: "High",
    URGENT: "Urgent",
  },
  priorities: ["Low", "Medium", "High", "Urgent"],
  priorityLabels: {
    Low: "Low",
    Medium: "Medium",
    High: "High",
    Urgent: "Urgent",
  },
  severity: {
    LOW: "Low",
    MEDIUM: "Medium",
    HIGH: "High",
    CRITICAL: "Critical",
  },
  severities: ["Low", "Medium", "High", "Critical"],
  severityLabels: {
    Low: "Low",
    Medium: "Medium",
    High: "High",
    Critical: "Critical",
  },
  commentType: {
    INTERNAL: "internal",
    PUBLIC: "public",
  },
  activeStates: ["open", "in_progress", "on_hold"],
  terminalStates: ["resolved", "closed"],
};

// Cache for constants (avoid refetching on every component mount)
let cachedConstants = null;
let cacheTimestamp = null;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to fetch and use ticket constants from the server
 * @returns {Object} { constants, loading, error, refetch }
 */
export function useTicketConstants() {
  const [constants, setConstants] = useState(
    cachedConstants || DEFAULT_CONSTANTS,
  );
  const [loading, setLoading] = useState(!cachedConstants);
  const [error, setError] = useState(null);

  const fetchConstants = useCallback(async (force = false) => {
    // Use cache if available and not expired
    if (
      !force &&
      cachedConstants &&
      cacheTimestamp &&
      Date.now() - cacheTimestamp < CACHE_DURATION_MS
    ) {
      setConstants(cachedConstants);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get("/api/tickets/constants");

      if (response.data?.success && response.data?.data) {
        const serverConstants = response.data.data;

        // Update cache
        cachedConstants = serverConstants;
        cacheTimestamp = Date.now();

        setConstants(serverConstants);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Failed to fetch ticket constants:", err);
      setError(err.message || "Failed to load ticket constants");
      // Keep using defaults/cached on error
      setConstants(cachedConstants || DEFAULT_CONSTANTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConstants();
  }, [fetchConstants]);

  // Helper functions that use the fetched constants
  const getWorkflowStateLabel = useCallback(
    (state) => {
      return constants.workflowStateLabels?.[state] || state || "-";
    },
    [constants],
  );

  const getWorkflowStateColor = useCallback((state) => {
    switch (state) {
      case "open":
        return "blue";
      case "in_progress":
        return "yellow";
      case "on_hold":
        return "orange";
      case "resolved":
        return "green";
      case "closed":
        return "gray";
      default:
        return "gray";
    }
  }, []);

  const getPriorityColor = useCallback((priority) => {
    switch (priority) {
      case "Urgent":
        return "red";
      case "High":
        return "orange";
      case "Medium":
        return "yellow";
      case "Low":
        return "gray";
      default:
        return "blue";
    }
  }, []);

  const getSeverityColor = useCallback((severity) => {
    switch (severity) {
      case "Critical":
        return "red";
      case "High":
        return "orange";
      case "Medium":
        return "yellow";
      case "Low":
        return "gray";
      default:
        return "gray";
    }
  }, []);

  const normalizeToWorkflowState = useCallback(
    (statusOrState) => {
      if (!statusOrState) return constants.workflowState?.OPEN || "open";

      const normalized = String(statusOrState)
        .toLowerCase()
        .replace(/\s+/g, "_");

      // Check if already a valid workflow state
      if (constants.workflowStates?.includes(normalized)) {
        return normalized;
      }

      // Legacy mapping
      const legacyMap = {
        open: "open",
        "in progress": "in_progress",
        in_progress: "in_progress",
        "on hold": "on_hold",
        on_hold: "on_hold",
        resolved: "resolved",
        closed: "closed",
      };

      return legacyMap[normalized] || "open";
    },
    [constants],
  );

  const getTicketWorkflowState = useCallback(
    (ticket) => {
      if (!ticket) return constants.workflowState?.OPEN || "open";
      return ticket.workflowState || normalizeToWorkflowState(ticket.status);
    },
    [constants, normalizeToWorkflowState],
  );

  const getTicketDueDate = useCallback((ticket) => {
    if (!ticket) return null;
    return ticket.dueDate || ticket.slaDueDate || null;
  }, []);

  return {
    // Raw constants
    constants,
    loading,
    error,
    refetch: () => fetchConstants(true),

    // Destructured for convenience
    WORKFLOW_STATE: constants.workflowState,
    WORKFLOW_STATES: constants.workflowStates,
    WORKFLOW_STATE_LABELS: constants.workflowStateLabels,
    STATUS_TABS: constants.statusTabs,
    PRIORITY: constants.priority,
    PRIORITIES: constants.priorities,
    SEVERITY: constants.severity,
    SEVERITIES: constants.severities,
    COMMENT_TYPE: constants.commentType,
    ACTIVE_STATES: constants.activeStates,
    TERMINAL_STATES: constants.terminalStates,
    WORKFLOW_TRANSITIONS: constants.workflowTransitions,
    SLA_POLICIES: constants.slaPolicies,
    DEFAULTS: constants.defaults,

    // Helper functions
    getWorkflowStateLabel,
    getWorkflowStateColor,
    getPriorityColor,
    getSeverityColor,
    normalizeToWorkflowState,
    getTicketWorkflowState,
    getTicketDueDate,
  };
}

export default useTicketConstants;
